import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";
import {
  RenewalWorkflowStep,
  getRenewalNoticeToClientPreview,
  getRenewalProgress,
  sendRenewalNoticeToClient,
  type RenewalNoticePdfItem,
} from "../../api/renewal";

export const RENEWAL_NOTICE_TO_CLIENT_FORM_ID =
  "renewal-notice-to-client-form";

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

function groupItemsByApplication(items: RenewalNoticePdfItem[]) {
  const map = new Map<string, { tmNo: string; items: RenewalNoticePdfItem[] }>();
  for (const it of items) {
    const existing = map.get(it.applicationId);
    if (existing) {
      existing.items.push(it);
    } else {
      map.set(it.applicationId, { tmNo: it.tmNo, items: [it] });
    }
  }
  return Array.from(map.entries()).map(([applicationId, value]) => ({
    applicationId,
    ...value,
  }));
}

export default function RenewalNoticeToClientView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: Props) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [items, setItems] = useState<RenewalNoticePdfItem[]>([]);
  const [totalPdfs, setTotalPdfs] = useState(0);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  useEffect(() => {
    onSentStatusChange?.(hasSent);
  }, [hasSent, onSentStatusChange]);

  useEffect(() => {
    if (!leadId) {
      setHasSent(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getRenewalProgress(leadId);
        const step = progress.steps.find(
          (s) => s.slug === RenewalWorkflowStep.RN_S3_02_NoticeToClient
        );
        if (!cancelled && step) setHasSent(step.isCompleted);
      } catch {
        if (!cancelled) setHasSent(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    getRenewalNoticeToClientPreview(leadId)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items ?? []);
        setTotalPdfs(res.totalPdfsToForward ?? 0);
        setPhoneNumber(res.recipientPhone ?? "");
        setEmail(res.recipientEmail ?? "");
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewError(
          err instanceof Error ? err.message : "Failed to load preview"
        );
        setItems([]);
        setTotalPdfs(0);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const groupedItems = useMemo(() => groupItemsByApplication(items), [items]);
  const previewMessages = useMemo(
    () => items.map((it) => it.previewMessage).filter(Boolean),
    [items],
  );
  const canSend = !previewError && totalPdfs > 0;

  const triggerSend = (mode: "send" | "resend") => {
    if (!leadId || sending) return;
    if (!canSend) {
      toast.error(
        previewError ??
          "Upload Renewal Intimation or Status of Application PDFs in Step 1 first."
      );
      return;
    }
    setConfirmMode(mode);
    setShowConfirmModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (hasSent) {
      onSuccess?.(leadId);
      return;
    }
    triggerSend("send");
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSending(true);
    (async () => {
      try {
        const result = await sendRenewalNoticeToClient(leadId);
        setHasSent(true);
        toast.success(
          confirmMode === "resend"
            ? `Notice resent — ${result.pdfsForwarded} PDF${
                result.pdfsForwarded === 1 ? "" : "s"
              } forwarded to client.`
            : `Notice sent — ${result.pdfsForwarded} PDF${
                result.pdfsForwarded === 1 ? "" : "s"
              } forwarded to client.`
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      } finally {
        setSending(false);
      }
    })();
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  return (
    <form
      id={RENEWAL_NOTICE_TO_CLIENT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-5">
        <p className="text-[16px] leading-5 text-[#222]">
          Message Preview
          {previewMessages.length > 1 && (
            <span className="ml-2 text-[12px] text-[#6D6D6D] font-normal">
              ({previewMessages.length} message
              {previewMessages.length === 1 ? "" : "s"} — one per PDF)
            </span>
          )}
        </p>
        <div className="rounded-[6px] border border-[#EEEEEE] bg-white p-[14px] min-h-[148px] flex flex-col gap-3">
          {previewLoading && previewMessages.length === 0 ? (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Loading preview...
            </p>
          ) : previewMessages.length > 0 ? (
            previewMessages.map((msg, idx) => (
              <pre
                key={idx}
                className={`font-sans text-[12px] leading-5 text-[#222] m-0 whitespace-pre-wrap break-words ${
                  idx > 0 ? "pt-3 border-t border-dashed border-[#EEE]" : ""
                }`}
                style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
              >
                {renderWhatsAppFormatting(msg)}
              </pre>
            ))
          ) : (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              {previewError ??
                "Upload Renewal Intimation or Status of Application PDFs in Step 1 to generate the notice."}
            </p>
          )}
        </div>
      </div>

      {groupedItems.length > 0 && (
        <div className="flex flex-col gap-3">
          <p
            className="text-[14px] font-medium leading-5 text-[#222]"
            style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            PDFs to forward
            <span className="ml-2 text-[#6D6D6D] font-normal">
              ({totalPdfs} file{totalPdfs === 1 ? "" : "s"})
            </span>
          </p>
          <div className="flex flex-col gap-2">
            {groupedItems.map((group) => (
              <div
                key={group.applicationId}
                className="rounded-[8px] border border-[#EEE] bg-white p-3 flex flex-col gap-2"
              >
                <div className="text-xs font-semibold text-[#222]">
                  TM No. {group.tmNo}
                </div>
                <ul className="flex flex-col gap-1">
                  {group.items.map((it) => (
                    <li
                      key={`${it.applicationId}-${it.kind}`}
                      className="text-xs text-[#222] flex items-center gap-2 min-w-0"
                    >
                      <span className="inline-block px-2 py-0.5 rounded-[4px] bg-[#F7F7F7] text-[#6D6D6D] text-[10px] uppercase tracking-wider whitespace-nowrap">
                        {it.kind === "renewal_intimation"
                          ? "Renewal Intimation"
                          : "Status of Application"}
                      </span>
                      <span className="truncate">{it.filename}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        <input
          type="email"
          value={email}
          readOnly
          placeholder="client@example.com"
          className="w-full min-w-0 sm:w-[260px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSent && (
          <button
            type="button"
            disabled={sending || !canSend}
            onClick={() => triggerSend("resend")}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Resend Message"}
          </button>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={previewMessages.length > 0 ? previewMessages : "—"}
        onConfirm={handleConfirmProceed}
        disableConfirm={!canSend}
      />
    </form>
  );
}
